/*
 * Dark Web Monitoring page.
 *
 * Headline stats (total breach mentions, emails breached, credential
 * leaks, email-only mentions), 18-month cumulative-exposure dual-bar chart and per-email
 * breach table. View toggle filters between all / creds-only / email-only.
 *
 * Calculations:
 *   - summary maps /getorgstats: totalBreaches=breachedUsers.allTotal,
 *     emailsBreached=breachedUsers.allUnique, credentialBreaches=passwords.withPass,
 *     emailOnlyBreaches=passwords.withoutPassOrHash, emailsScanned=assets.total.
 *   - byEmail maps /breachedusers rows (sorted by mentions DESC); the view
 *     filter is a single in-component predicate over byEmail.
 *
 * Live data:
 *   - Summary stats + per-email breach rows are pulled from dwm-api
 *     (`useGetDwmOrgStatsQuery` / `useGetDwmBreachedUsersQuery`), both
 *     tenancy-scoped. Cached 6 hours (breach feeds refresh infrequently).
 *   - The dwm-api has no name/department on breach rows, so those columns
 *     are joined from tenancy user data (`useGetUsersQuery`) by email.
 *   - The cumulative exposure chart is backed by sp_GetBreachTrend via
 *     `useGetDwmBreachTrendQuery`, scoped to the shell's selected date range.
 *     The proc returns all-time daily data (cached per org in BreachService);
 *     the API filters to the range, rolls it up to an auto-chosen granularity,
 *     zero-fills gaps and echoes the granularity back. The browser only maps
 *     display labels; TrendChart still computes the cumulative running total.
 *   - The API carries no distinct-source count or latest-breach date per
 *     user, so those two columns show '-' until an endpoint provides them.
 *
 * Gated by FEATURES.darkWebMonitoring.
 */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Grid, Button, Tooltip, CircularProgress } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined';
import { motion } from 'framer-motion';
import MainCard from '../../../components/cards/MainCard';
import { useGetDwmOrgStatsQuery, useGetDwmBreachedUsersQuery, useGetDwmBreachTrendQuery } from '../../../store/services/dwm-service';
import { useGetUsersQuery } from '../../../store/services/user-service';

// The API returns a complete, zero-filled, chronologically ordered series for the
// selected range and echoes the granularity it chose (collation + padding happen
// server-side in BreachService), so the browser only formats a display label per
// bucket. Bucket keys: "YYYY-MM" (month), "YYYY-Www" (ISO week), "YYYY-MM-DD" (day).
function formatBucketLabel(bucket, granularity) {
    if (granularity === 'week') return bucket.slice(5); // "W07"
    if (granularity === 'day') {
        const d = new Date(`${bucket}T00:00:00Z`);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    }
    const [y, m] = bucket.split('-').map(Number); // month
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

// The dwm-api breach rows carry no display name, so fall back to a readable
// name derived from the email local-part when the tenancy join misses.
function deriveNameFromEmail(email) {
    if (!email) return '';
    const local = String(email).split('@')[0];
    return local
        .split(/[._-]+/)
        .filter(Boolean)
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ') || email;
}

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

// Left-aligned "View" affordance for drillable rows: replaces whole-row
// click so the click target is explicit, matching the rest of the portal.
function RowViewButton({ onClick }) {
    return (
        <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            sx={{
                borderRadius: '4px', borderColor: 'divider', color: 'text.secondary',
                fontSize: 11, fontWeight: 600, py: '2px', px: '10px', minWidth: 0, textTransform: 'none',
                '&:hover': { borderColor: 'secondary.main', color: 'secondary.main', bgcolor: 'transparent' },
            }}
        >
            View
        </Button>
    );
}

function StatCard({ label, value, sub, color, icon: Icon }) {
    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {Icon && (
                    <Box aria-hidden="true" sx={{ position: 'absolute', top: 18, right: 18, display: 'flex', color: color || 'text.disabled', opacity: color ? 0.85 : 0.5 }}>
                        <Icon sx={{ fontSize: 22 }} />
                    </Box>
                )}
                <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', pr: 4 }}>{label}</Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, mt: 0.5, color: color || 'text.primary' }}>
                    {typeof value === 'number' ? value.toLocaleString() : value}
                </Typography>
                {sub && <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 'auto', pt: 0.5 }}>{sub}</Typography>}
            </Box>
        </MainCard>
    );
}

// Round an axis maximum up to a clean tick value so the gridline labels read
// as whole, human numbers (10, 25, 250) rather than the raw running total.
function niceMax(v) {
    if (v <= 5) return 5;
    const mag = Math.pow(10, Math.floor(Math.log10(v)));
    const n = v / mag;
    const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
    return step * mag;
}

// Cumulative-exposure dual-bar chart. The raw monthly counts are near-uniform
// noise, so per-month bars exaggerate jitter; the running total instead shows
// the real story (total exposure only ever grows) as grouped bars per month:
// credential leaks beside lower-severity email-only.
function TrendChart({ data }) {
    const PAD_L = 34;   // gutter for the y-axis tick labels
    const LABEL_H = 18; // reserved band for month labels

    // Build running totals once per data change.
    const cum = useMemo(() => {
        let creds = 0;
        let total = 0;
        return data.map((d) => {
            creds += d.creds;
            total += d.creds + d.emailOnly;
            return { label: d.label, key: d.key, creds, total, mCreds: d.creds, mEmail: d.emailOnly };
        });
    }, [data]);

    const max = niceMax(cum.length ? cum[cum.length - 1].total : 1);

    // Fill the width the card gives us; keep a fixed drawing height so the
    // curve aspect ratio stays consistent regardless of container width.
    const chartRef = useRef(null);
    const [w, setW] = useState(760);
    useEffect(() => {
        const el = chartRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            const r = entries[0]?.contentRect;
            if (r && r.width > 0) setW(r.width);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const W = Math.max(280, w);
    const H = 150;
    const plotW = W - PAD_L;

    // Grouped-bar geometry: each month gets a slot holding a credential-leak
    // bar beside an email-only bar, both sized against the shared y scale.
    const n = cum.length || 1;
    const slotW = plotW / n;
    const barW = Math.min(13, slotW * 0.36);
    const gap = Math.max(1, slotW * 0.06);
    const slotCenter = (i) => PAD_L + slotW * (i + 0.5);

    const ticks = [0, 0.25, 0.5, 0.75, 1];

    return (
        <Box ref={chartRef} sx={{ width: '100%' }}>
            <Box component="svg" viewBox={`0 0 ${W} ${H + LABEL_H}`} preserveAspectRatio="none" sx={{ width: '100%', display: 'block' }}>
                {/* Gridlines + y-axis ticks */}
                {ticks.map((t) => (
                    <g key={t}>
                        <line x1={PAD_L} x2={W} y1={H - t * H} y2={H - t * H} stroke="var(--chart-grid)" strokeWidth="1" />
                        <text x={PAD_L - 6} y={H - t * H + 3} textAnchor="end" fontSize="9" fontFamily="Inter,sans-serif" fill="var(--chart-tick)">
                            {Math.round(t * max)}
                        </text>
                    </g>
                ))}

                {/* Grouped bars per month: credential leaks (red) beside
                    email-only (muted). Each carries the month's hover detail. */}
                {cum.map((d, i) => {
                    const emailVal = d.total - d.creds;
                    const credH = (d.creds / max) * H;
                    const emailH = (emailVal / max) * H;
                    const cx = slotCenter(i);
                    const credX = cx - barW - gap / 2;
                    const emailX = cx + gap / 2;
                    const credTitle = `${d.label}: ${d.creds} credential leaks (cumulative) - +${d.mCreds} this month`;
                    const emailTitle = `${d.label}: ${emailVal} email-only (cumulative) - +${d.mEmail} this month`;
                    return (
                        <g key={d.key}>
                            {/* Themed MUI tooltips on the bars themselves (replaces the
                                native <title>, which ignored light/dark mode). Each bar
                                is its own hover target and reports its own series. */}
                            <Tooltip title={credTitle} placement="top" arrow>
                                <rect x={credX} y={H - credH} width={barW} height={credH} rx={2} fill="var(--red)" opacity={0.9} style={{ cursor: 'default' }} />
                            </Tooltip>
                            <Tooltip title={emailTitle} placement="top" arrow>
                                <rect x={emailX} y={H - emailH} width={barW} height={emailH} rx={2} fill="var(--muted)" opacity={0.55} style={{ cursor: 'default' }} />
                            </Tooltip>
                            {i % 3 === 0 && (
                                <text x={cx} y={H + 14} textAnchor="middle" fontSize="9" fontFamily="Inter,sans-serif" fill="var(--chart-tick)">
                                    {d.label}
                                </text>
                            )}
                        </g>
                    );
                })}
            </Box>
        </Box>
    );
}

export default function DwmPage({ drill, startDate, endDate }) {
    const [view, setView] = useState('all'); // all | creds | email_only

    // Resolve the active tenancy the same way the rest of the portal does:
    // explicit URL override, then the Redux tenancy slice, then the logged-in
    // user. Every dwm-api endpoint is tenancy-scoped so this must be present.
    const reduxTenancyId = useSelector((state) => state.tenancy.tenancyId);
    const tenancyId = useMemo(() => {
        const urlId = new URLSearchParams(window.location.search).get('tenancyId');
        if (urlId && urlId !== 'undefined') return parseInt(urlId, 10);
        if (reduxTenancyId) return parseInt(reduxTenancyId, 10);
        try { return JSON.parse(localStorage.getItem('user'))?.tenancyId; } catch { return undefined; }
    }, [reduxTenancyId]);

    const { data: orgStats, isLoading: statsLoading, isError: statsError } =
        useGetDwmOrgStatsQuery({ tenancyId }, { skip: !tenancyId });
    const { data: breached, isLoading: breachedLoading, isError: breachedError } =
        useGetDwmBreachedUsersQuery({ tenancyId, page: 1, limit: 100 }, { skip: !tenancyId });
    const { data: trendData, isLoading: trendLoading } =
        useGetDwmBreachTrendQuery({ tenancyId, startDate, endDate, granularity: 'auto' }, { skip: !tenancyId });
    // Pulled once so breach rows can be joined to a name/department by email.
    const { data: tenancyUsers } = useGetUsersQuery(
        { tenancyId, perPage: 100000, page: 0, includes: '', query: '', extendedResults: true },
        { skip: !tenancyId }
    );

    const userMap = useMemo(() => {
        const m = new Map();
        for (const u of tenancyUsers?.data || []) {
            if (u?.email) m.set(String(u.email).toLowerCase(), u);
        }
        return m;
    }, [tenancyUsers]);

    // Headline numbers from /getorgstats. Credential vs email-only split comes
    // from the password analysis; emails scanned from the monitored asset count.
    const summary = useMemo(() => {
        const bu = orgStats?.breachedUsers || {};
        const pw = orgStats?.passwords || {};
        const assets = orgStats?.assets || {};
        return {
            totalBreaches: bu.allTotal ?? 0,
            emailsBreached: bu.allUnique ?? 0,
            credentialBreaches: pw.withPass ?? 0,
            emailOnlyBreaches: pw.withoutPassOrHash ?? 0,
            emailsScanned: assets.total ?? pw.total ?? 0,
        };
    }, [orgStats]);

    // Per-email rows from /breachedusers, joined to tenancy name/department.
    // mentions = total breach records; withCreds = records exposing a password.
    const byEmail = useMemo(() => {
        return (breached?.users || []).map((u) => {
            const match = userMap.get(String(u.email || '').toLowerCase());
            const joinedName = match ? `${match.firstName || ''} ${match.lastName || ''}`.trim() : '';
            return {
                email: u.email,
                name: joinedName || deriveNameFromEmail(u.email),
                dept: match?.department || '-',
                mentions: u.breaches ?? 0,
                withCreds: u.passwordAvailable ?? 0,
                // dwm-api has no distinct-source count or latest-breach date yet.
                sources: '-',
                latest: '-',
            };
        }).sort((a, b) => b.mentions - a.mentions);
    }, [breached, userMap]);

    const filtered = useMemo(() => {
        if (view === 'all') return byEmail;
        if (view === 'creds') return byEmail.filter((e) => e.withCreds > 0);
        return byEmail.filter((e) => e.withCreds < e.mentions);
    }, [view, byEmail]);

    // API already returns a complete ordered series; just attach display labels
    // using the granularity the API chose.
    const trend = useMemo(() => {
        const gran = trendData?.granularity || 'month';
        return (trendData?.points || []).map((r) => ({
            key: r.bucket,
            label: formatBucketLabel(r.bucket, gran),
            creds: Number(r.creds) || 0,
            emailOnly: Number(r.emailOnly) || 0
        }));
    }, [trendData]);

    const loading = statsLoading || breachedLoading || trendLoading;
    const errored = statsError && breachedError;

    if (!tenancyId) {
        return (
            <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', mb: 0.5 }}>No tenancy selected</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Select a tenancy to view dark web monitoring data.</Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ p: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={28} color="secondary" />
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Loading dark web monitoring data...</Typography>
            </Box>
        );
    }

    if (errored) {
        return (
            <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', mb: 0.5 }}>Could not load dark web monitoring data</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>The monitoring service is unavailable or this tenancy has no breach feed configured.</Typography>
            </Box>
        );
    }

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <motion.div variants={fadeUp}>
                <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>Dark web monitoring</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                        Continuous scan of paste sites and breach corpora for emails belonging to {summary.emailsScanned.toLocaleString()} active users.
                    </Typography>
                </Box>
            </motion.div>

            {/* Headline numbers */}
            <motion.div variants={fadeUp}>
                <Grid container spacing={1} alignItems="stretch" sx={{ '& > .MuiGrid-item': { display: 'flex' } }}>
                    <Grid item xs={6} md={3}><StatCard label="Total breach mentions" value={summary.totalBreaches} color="#FF5252" sub="across all sources" icon={WarningAmberOutlinedIcon} /></Grid>
                    <Grid item xs={6} md={3}><StatCard label="Emails breached" value={summary.emailsBreached} sub={summary.emailsScanned ? `${Math.round((summary.emailsBreached / summary.emailsScanned) * 100)}% of monitored emails` : 'monitored emails'} icon={AlternateEmailOutlinedIcon} /></Grid>
                    <Grid item xs={6} md={3}><StatCard label="Credential leaks" value={summary.credentialBreaches} color="#FF5252" sub="email + password exposed" icon={LockOutlinedIcon} /></Grid>
                    <Grid item xs={6} md={3}><StatCard label="Email-only mentions" value={summary.emailOnlyBreaches} sub="lower severity, password not exposed" icon={MailOutlineIcon} /></Grid>
                </Grid>
            </motion.div>

            {/* Trend */}
            <motion.div variants={fadeUp}>
                <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                    <Box sx={{ px: 3, pt: 2.5, pb: 1.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                                Cumulative exposure
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                Running total of breached records - over the selected period - hover for detail
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, color: 'text.secondary' }}>
                                <Box sx={{ width: 10, height: 10, bgcolor: 'var(--red)', borderRadius: '2px' }} />
                                Credential leaks
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, color: 'text.secondary' }}>
                                <Box sx={{ width: 10, height: 10, bgcolor: 'var(--muted)', opacity: 0.55, borderRadius: '2px' }} />
                                Email-only
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ p: 3 }}>
                        <TrendChart data={trend} />
                    </Box>
                </MainCard>
            </motion.div>

            {/* Breakdown by email */}
            <motion.div variants={fadeUp}>
                <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                    <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                                Most-mentioned emails
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                {filtered.length} emails appear in {filtered.reduce((s, e) => s + e.mentions, 0)} breach records - click a row to drill in
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {[{ k: 'all', l: 'All' }, { k: 'creds', l: 'Credentials leaked' }, { k: 'email_only', l: 'Email only' }].map((opt) => {
                                const isActive = view === opt.k;
                                return (
                                    <Button
                                        key={opt.k}
                                        size="small"
                                        onClick={() => setView(opt.k)}
                                        sx={{
                                            fontSize: 11, textTransform: 'none', px: 1.25, py: '3px', minWidth: 0,
                                            borderRadius: '4px',
                                            border: '1px solid', borderColor: isActive ? 'secondary.main' : 'divider',
                                            bgcolor: isActive ? 'secondary.main' : 'transparent',
                                            color: isActive ? 'common.white' : 'text.secondary',
                                            '&:hover': { borderColor: 'secondary.main', bgcolor: isActive ? 'secondary.main' : 'transparent', color: isActive ? 'common.white' : 'secondary.main' },
                                        }}
                                    >
                                        {opt.l}
                                    </Button>
                                );
                            })}
                        </Box>
                    </Box>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <Box component="thead">
                                <Box component="tr">
                                    <Box component="th" aria-hidden="true" sx={{ borderBottom: '1px solid', borderColor: 'divider', width: '1px' }} />
                                    {['Email', 'Name', 'Department', 'Mentions', 'Credentials', 'Distinct sources', 'Latest'].map((h) => (
                                        <Box key={h} component="th" sx={{ fontSize: 11, fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em', px: 2, py: 1.25, textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap' }}>{h}</Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box component="tbody">
                                {filtered.slice(0, 50).map((e) => (
                                    <Box component="tr" key={e.email}
                                        sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' }, '&:last-child': { borderBottom: 'none' } }}>
                                        <Box component="td" sx={{ px: 2, py: 1.25, width: '1px', whiteSpace: 'nowrap' }}>
                                            <RowViewButton onClick={() => drill('user-detail', e.name, { type: 'user', value: e.email })} />
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, color: 'text.primary', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>{e.email}</Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, color: 'text.secondary' }}>{e.name}</Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, color: 'text.secondary' }}>{e.dept}</Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, fontWeight: 700, color: 'text.primary' }}>{e.mentions}</Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25 }}>
                                            {e.withCreds > 0 ? (
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: '6px', py: '1px', borderRadius: '4px', fontSize: 10, fontWeight: 700, bgcolor: 'rgba(255,82,82,.1)', color: '#E53935', border: '1px solid rgba(255,82,82,.25)' }}>
                                                    <LockOutlinedIcon sx={{ fontSize: 11 }} />
                                                    {e.withCreds} with password
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.disabled', fontSize: 11 }}>
                                                    <MailOutlineIcon sx={{ fontSize: 11 }} />
                                                    email only
                                                </Box>
                                            )}
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, color: 'text.secondary' }}>{e.sources}</Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, color: 'text.disabled', fontSize: 11 }}>{e.latest}</Box>
                                    </Box>
                                ))}
                                {filtered.length === 0 && (
                                    <Box component="tr"><Box component="td" colSpan={8} sx={{ px: 2, py: 4, textAlign: 'center', fontSize: 12, color: 'text.disabled' }}>No emails match this filter</Box></Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </MainCard>
            </motion.div>
        </motion.div>
    );
}
